import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_KEY, LIVE_API_BASE_URL, getApiHeaders } from '../config/api';
import type { StickerPack, StickerTextSlot } from './LiveStickers';
import { packPriceLabel } from '../utils/stickerPackPricing';

const liveApiBase = `${LIVE_API_BASE_URL.replace(/\/$/, '')}/v1/live`;

type PresignResponse = {
  stickerId: string;
  uploadUrl: string;
  fileKey: string;
  fileUrl: string;
};

/** Matches the backend's clamp ranges so the admin never saves something the server will alter. */
const SLOT_DEFAULTS: StickerTextSlot = {
  x: 0.15,
  y: 0.3,
  width: 0.7,
  height: 0.4,
  fontKey: 'inter',
  colorHex: '#1B1B1B',
  align: 'center',
  maxLines: 2,
  maxFontSize: 28,
  minFontSize: 8,
};

const FONT_KEYS = ['inter', 'poppins', 'roboto'];
const ALIGNMENTS = ['left', 'center', 'right'];

/**
 * Binary-searches the font size the way the app does, so the preview shrinks text exactly as a
 * viewer will see it. Measured against a 390pt reference screen, which is what maxFontSize means.
 */
const fitFontSize = (
  ctx: CanvasRenderingContext2D,
  text: string,
  boxW: number,
  boxH: number,
  slot: StickerTextSlot,
  scale: number
): { size: number; lines: string[] } => {
  const wrap = (size: number): string[] => {
    ctx.font = `600 ${size}px ${slot.fontKey}, sans-serif`;
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let current = '';
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (ctx.measureText(candidate).width <= boxW || !current) {
        current = candidate;
      } else {
        lines.push(current);
        current = word;
      }
    }
    if (current) lines.push(current);
    return lines;
  };

  let lo = slot.minFontSize * scale;
  let hi = slot.maxFontSize * scale;
  let best = lo;
  let bestLines = wrap(lo);

  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    const lines = wrap(mid);
    const fits = lines.length <= slot.maxLines && lines.length * mid * 1.2 <= boxH;
    if (fits) {
      best = mid;
      bestLines = lines;
      lo = mid;
    } else {
      hi = mid;
    }
    if (hi - lo < 0.25) break;
  }

  // Below the floor the app ellipsises rather than shrinking further.
  if (bestLines.length > slot.maxLines) {
    bestLines = bestLines.slice(0, slot.maxLines);
    bestLines[bestLines.length - 1] = `${bestLines[bestLines.length - 1].replace(/\s+\S*$/, '')}…`;
  }
  return { size: best, lines: bestLines };
};

const CreateLiveSticker: React.FC = () => {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [packs, setPacks] = useState<StickerPack[]>([]);
  const [packId, setPackId] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'plain' | 'text'>('plain');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [aspectRatio, setAspectRatio] = useState(1);
  const [slot, setSlot] = useState<StickerTextSlot>(SLOT_DEFAULTS);
  const [sampleText, setSampleText] = useState("49 900 so'm");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [presign, setPresign] = useState<PresignResponse | null>(null);
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  useEffect(() => {
    const load = async () => {
      const headers = headerWithAuth();
      if (!headers) return;
      try {
        const res = await fetch(`${liveApiBase}/admin/sticker-packs`, { headers });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to load packs');
        const list = (json.data?.packs as StickerPack[]) || [];
        setPacks(list);
        if (list.length && !packId) setPackId(list[0].id);
      } catch (error) {
        toast.error((error as Error).message || 'Failed to load packs');
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerWithAuth]);

  const handleFileChange = (file: File | null) => {
    setPresign(null);
    setImageFile(file);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    if (!file) {
      setImagePreview('');
      return;
    }
    // PNG and WebP only — the backend rejects anything else, because a format without an alpha
    // channel renders as an opaque box sitting over the video.
    if (!/image\/(png|webp)/.test(file.type)) {
      toast.error('Stickers must be PNG or WebP with alpha. A JPEG renders as a white box over the video.');
      setImagePreview('');
      setImageFile(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setImagePreview(url);
    const img = new Image();
    img.onload = () => setAspectRatio(Number((img.width / img.height).toFixed(4)));
    img.src = url;
  };

  // ── The drag-a-rectangle slot editor ──────────────────────────────────────

  const pointToFraction = (e: React.MouseEvent): { x: number; y: number } | null => {
    const el = imageRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (kind !== 'text') return;
    const p = pointToFraction(e);
    if (p) setDrag(p);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!drag) return;
    const p = pointToFraction(e);
    if (!p) return;
    setSlot((prev) => ({
      ...prev,
      x: Math.min(drag.x, p.x),
      y: Math.min(drag.y, p.y),
      width: Math.abs(p.x - drag.x),
      height: Math.abs(p.y - drag.y),
    }));
  };

  const handleMouseUp = () => setDrag(null);

  // ── Live preview: auto-fits sample text exactly as the app will ───────────

  useEffect(() => {
    if (kind !== 'text') return;
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img || !imagePreview) return;

    const w = img.clientWidth;
    const h = img.clientHeight;
    if (!w || !h) return;
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, w, h);

    const boxX = slot.x * w;
    const boxY = slot.y * h;
    const boxW = slot.width * w;
    const boxH = slot.height * h;
    if (boxW < 4 || boxH < 4) return;

    // The rendered image stands in for a 390pt-wide phone screen; maxFontSize is defined against
    // that reference, so the preview scales by the same ratio the client will.
    const scale = w / 390;
    const { size, lines } = fitFontSize(ctx, sampleText || ' ', boxW, boxH, slot, scale);

    ctx.font = `600 ${size}px ${slot.fontKey}, sans-serif`;
    ctx.fillStyle = slot.colorHex;
    ctx.textBaseline = 'middle';
    ctx.textAlign = slot.align as CanvasTextAlign;

    const lineHeight = size * 1.2;
    const startY = boxY + boxH / 2 - ((lines.length - 1) * lineHeight) / 2;
    const textX = slot.align === 'left' ? boxX : slot.align === 'right' ? boxX + boxW : boxX + boxW / 2;
    lines.forEach((line, i) => ctx.fillText(line, textX, startY + i * lineHeight));
  }, [kind, slot, sampleText, imagePreview]);

  const slotSummary = useMemo(
    () =>
      `x ${slot.x.toFixed(2)} · y ${slot.y.toFixed(2)} · w ${slot.width.toFixed(2)} · h ${slot.height.toFixed(2)}`,
    [slot]
  );

  const handleUpload = async () => {
    if (!name.trim()) return toast.error('Sticker name is required.');
    if (!packId) return toast.error('Choose a pack.');
    if (!imageFile) return toast.error('Select a PNG or WebP image.');

    const headers = headerWithAuth();
    if (!headers) return;

    setUploading(true);
    try {
      const contentType = imageFile.type === 'image/webp' ? 'image/webp' : 'image/png';
      const presignRes = await fetch(`${liveApiBase}/admin/stickers/presign`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ contentType }),
      });
      const presignJson = await presignRes.json();
      if (!presignRes.ok) throw new Error(presignJson.message || 'Failed to get upload URL');
      const result = presignJson.data as PresignResponse;

      // The content type on the PUT must match the one bound into the signature, or the object is
      // served back as the wrong type and loses its alpha channel.
      const uploadRes = await fetch(result.uploadUrl, {
        method: 'PUT',
        body: await imageFile.arrayBuffer(),
        headers: { 'Content-Type': contentType },
      });
      if (!uploadRes.ok) throw new Error('Failed to upload image');

      setPresign(result);
      toast.success('Image uploaded — save the sticker to complete.');
    } catch (error) {
      toast.error((error as Error).message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!presign) return toast.error('Upload the image first.');
    if (kind === 'text' && (slot.width < 0.02 || slot.height < 0.02)) {
      return toast.error('Drag a writable area over the artwork first.');
    }

    const headers = headerWithAuth();
    if (!headers) return;

    setSaving(true);
    try {
      const res = await fetch(`${liveApiBase}/admin/stickers`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          stickerId: presign.stickerId,
          packId,
          name: name.trim(),
          fileKey: presign.fileKey,
          kind,
          aspectRatio,
          sortOrder: 0,
          isActive: true,
          textSlot: kind === 'text' ? slot : null,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to save sticker');
      toast.success('Sticker saved');
      navigate('/live-stickers');
    } catch (error) {
      toast.error((error as Error).message || 'Failed to save sticker');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full px-4 py-2 text-sm rounded-lg bg-white/10 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#FF9C15]';
  const labelClass = 'block text-xs font-semibold text-gray-300 mb-1.5';

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        <div>
          <h1 className='text-2xl font-bold text-white'>Upload Sticker</h1>
          <p className='text-sm text-gray-400'>PNG or WebP with alpha, ~1024px longest edge, ideally under 300 KB.</p>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 space-y-4'>
            <div>
              <label className={labelClass}>Pack</label>
              <select value={packId} onChange={(e) => setPackId(e.target.value)} className={inputClass}>
                {packs.length === 0 && <option value=''>No packs — create one first</option>}
                {packs.map((p) => (
                  <option key={p.id} value={p.id} className='bg-slate-800'>
                    {p.name} ({packPriceLabel(p)})
                  </option>
                ))}
              </select>
              {packs.find((p) => p.id === packId)?.isPremium && (
                <p className='mt-1 text-xs text-amber-300'>
                  Paid pack: this sticker will be premium. Paying Plus subscribers can send it free; everyone else must buy the pack.
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder='Heart' className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>Type</label>
              <div className='flex gap-2'>
                {(['plain', 'text'] as const).map((k) => (
                  <button
                    key={k}
                    onClick={() => setKind(k)}
                    className={`flex-1 px-3 py-2 text-xs font-semibold rounded-lg transition ${
                      kind === k
                        ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    {k === 'plain' ? 'Plain image' : 'Writable (text)'}
                  </button>
                ))}
              </div>
              {kind === 'text' && (
                <p className='text-xs text-gray-500 mt-2'>
                  Drag a rectangle on the artwork to mark where the host's text goes.
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>Image</label>
              <input
                ref={fileRef}
                type='file'
                accept='image/png,image/webp'
                onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                className='block w-full text-xs text-gray-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-white/10 file:text-white hover:file:bg-white/20'
              />
              {imageFile && (
                <p className='text-xs text-gray-500 mt-1.5'>
                  {(imageFile.size / 1024).toFixed(0)} KB · aspect ratio {aspectRatio}
                </p>
              )}
            </div>

            {kind === 'text' && (
              <div className='space-y-3 pt-2 border-t border-white/10'>
                <div>
                  <label className={labelClass}>Sample text (preview only)</label>
                  <input value={sampleText} onChange={(e) => setSampleText(e.target.value)} className={inputClass} />
                </div>
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <label className={labelClass}>Font</label>
                    <select value={slot.fontKey} onChange={(e) => setSlot({ ...slot, fontKey: e.target.value })} className={inputClass}>
                      {FONT_KEYS.map((f) => (
                        <option key={f} value={f} className='bg-slate-800'>{f}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Align</label>
                    <select value={slot.align} onChange={(e) => setSlot({ ...slot, align: e.target.value })} className={inputClass}>
                      {ALIGNMENTS.map((a) => (
                        <option key={a} value={a} className='bg-slate-800'>{a}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Colour</label>
                    <input type='color' value={slot.colorHex} onChange={(e) => setSlot({ ...slot, colorHex: e.target.value.toUpperCase() })} className='w-full h-9 rounded-lg bg-white/10 border border-white/10' />
                  </div>
                  <div>
                    <label className={labelClass}>Max lines ({slot.maxLines})</label>
                    <input type='range' min={1} max={6} value={slot.maxLines} onChange={(e) => setSlot({ ...slot, maxLines: Number(e.target.value) })} className='w-full mt-2' />
                  </div>
                  <div>
                    <label className={labelClass}>Max font ({slot.maxFontSize})</label>
                    <input type='range' min={6} max={96} value={slot.maxFontSize} onChange={(e) => setSlot({ ...slot, maxFontSize: Math.max(Number(e.target.value), slot.minFontSize) })} className='w-full mt-2' />
                  </div>
                  <div>
                    <label className={labelClass}>Min font ({slot.minFontSize})</label>
                    <input type='range' min={6} max={96} value={slot.minFontSize} onChange={(e) => setSlot({ ...slot, minFontSize: Math.min(Number(e.target.value), slot.maxFontSize) })} className='w-full mt-2' />
                  </div>
                </div>
                <p className='text-xs text-gray-500 font-mono'>{slotSummary}</p>
              </div>
            )}
          </div>

          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5'>
            <label className={labelClass}>Preview</label>
            {!imagePreview ? (
              <div className='h-64 flex items-center justify-center text-xs text-gray-500 border border-dashed border-white/10 rounded-xl'>
                Select an image to preview
              </div>
            ) : (
              <div
                className='relative inline-block select-none w-full'
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{
                  backgroundImage:
                    'linear-gradient(45deg, #334155 25%, transparent 25%), linear-gradient(-45deg, #334155 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #334155 75%), linear-gradient(-45deg, transparent 75%, #334155 75%)',
                  backgroundSize: '16px 16px',
                  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
                  cursor: kind === 'text' ? 'crosshair' : 'default',
                }}
              >
                <img ref={imageRef} src={imagePreview} alt='sticker' className='w-full h-auto block pointer-events-none' />
                <canvas ref={canvasRef} className='absolute inset-0 w-full h-full pointer-events-none' />
                {kind === 'text' && slot.width > 0.01 && slot.height > 0.01 && (
                  <div
                    className='absolute border-2 border-dashed border-[#FF9C15] bg-[#FF9C15]/10 pointer-events-none'
                    style={{
                      left: `${slot.x * 100}%`,
                      top: `${slot.y * 100}%`,
                      width: `${slot.width * 100}%`,
                      height: `${slot.height * 100}%`,
                    }}
                  />
                )}
              </div>
            )}
            <p className='text-xs text-gray-500 mt-3'>
              The checkerboard shows transparency. If the artwork has a solid background here, it will
              render as a box over the live video.
            </p>
          </div>
        </div>

        <div className='flex flex-col sm:flex-row gap-3'>
          <button
            onClick={handleUpload}
            disabled={uploading || !imageFile}
            className='px-5 py-2.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-40'
          >
            {uploading ? 'Uploading...' : presign ? 'Re-upload image' : '1 · Upload image'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !presign}
            className='px-5 py-2.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-40'
          >
            {saving ? 'Saving...' : '2 · Save sticker'}
          </button>
          <button
            onClick={() => navigate('/live-stickers')}
            className='px-5 py-2.5 text-xs font-semibold rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition'
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateLiveSticker;
